<script setup>
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import Section from "../internal/Section.vue";
import EditableField from "../fields/EditableField.vue";
import ReadonlyField from "../fields/ReadonlyField.vue";
import Button from "@/shared/ui/button/Button.vue";

const { t } = useI18n();

defineProps({
  data: { type: Object, required: true },
  editMode: { type: Boolean, default: false },
  isOwn: { type: Boolean, default: false },
});

const emit = defineEmits([
  "update-array-item",
  "add-array-item",
  "remove-array-item",
]);

const TEMPLATE = { status: "", number: "", update_date: "", details: "" };
const STATUS_KEYS = ["Выдана", "Действует", "Приостановлена", "Прекращена"];
const STATUSES = computed(() =>
  STATUS_KEYS.map((s) => ({ value: s, label: t(s) }))
);

function setItem(idx, key, value) {
  emit("update-array-item", "license_history", idx, key, value);
}
function addItem() {
  emit("add-array-item", "license_history", TEMPLATE);
}
function removeItem(idx) {
  emit("remove-array-item", "license_history", idx);
}
</script>

<template>
  <Section
    icon="huge-journal"
    :title="$t('История лицензии')"
    :sub="`${(data.license_history || []).length} ${$t('записей')}`"
  >
    <template v-if="editMode" #actions>
      <Button variant="outline" size="sm" @click="addItem">
        <SvgIcon
          name="huge-plus"
          class="mr-1"
          style="width: 13px; height: 13px; fill: none"
        />{{ $t("Добавить") }}
      </Button>
    </template>

    <div class="flex flex-col gap-2.5">
      <div
        v-for="(h, i) in data.license_history || []"
        :key="i"
        class="border border-border p-3.5 hover:border-input transition-colors"
        style="border-radius: var(--radius)"
      >
        <div class="flex items-center gap-2.5 mb-3">
          <div
            class="w-7 h-7 bg-card border border-border flex items-center justify-center text-[12px] font-semibold text-foreground/70"
            style="border-radius: var(--radius)"
          >
            {{ (data.license_history || []).length - i }}
          </div>
          <div class="text-[13px] font-semibold text-foreground">
            {{ $t(h.status || "Запись") }}
            <span class="text-muted-foreground font-normal ml-2">{{
              h.update_date || "—"
            }}</span>
          </div>
          <div class="ml-auto">
            <Button
              v-if="editMode"
              variant="ghost"
              size="icon-sm"
              class="w-7 h-7"
              @click="removeItem(i)"
            >
              <svg-icon name="huge-close" class="w-5 h-5" />
            </Button>
          </div>
        </div>
        <div class="grid grid-cols-2 gap-x-5 gap-y-3.5 max-sm:grid-cols-1">
          <template v-if="editMode">
            <EditableField
              :label="$t('Статус')"
              :model-value="h.status"
              :options="STATUSES"
              @update:model-value="setItem(i, 'status', $event)"
            />
            <EditableField
              :label="$t('Номер лицензии')"
              :model-value="h.number"
              @update:model-value="setItem(i, 'number', $event)"
            />
            <EditableField
              :label="$t('Дата обновления')"
              type="date"
              size="sm"
              :model-value="h.update_date"
              @update:model-value="setItem(i, 'update_date', $event)"
            />
            <EditableField
              :label="$t('Подробности')"
              :model-value="h.details"
              @update:model-value="setItem(i, 'details', $event)"
            />
          </template>
          <template v-else>
            <ReadonlyField
              :label="$t('Статус')"
              :value="h.status ? $t(h.status) : h.status"
            />
            <ReadonlyField :label="$t('Номер лицензии')" :value="h.number" />
            <ReadonlyField
              :label="$t('Дата обновления')"
              :value="h.update_date"
              type="date"
              size="sm"
            />
            <ReadonlyField :label="$t('Подробности')" :value="h.details" />
          </template>
        </div>
      </div>

      <div
        v-if="!(data.license_history || []).length"
        class="text-sm text-muted-foreground py-2"
      >
        {{ $t("Записей нет") }}
      </div>
    </div>
  </Section>
</template>
